import { useCallback, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getThumbnailUrl } from '../utils/thumbnailUrl';

interface ImageWithUrl {
  id: string;
  url: string;
}

interface UseSignedUrlsOptions {
  useThumbnails?: boolean;
}

// Global cache for signed URLs
const signedUrlCache = new Map<string, string>();

async function fetchSignedUrl(gsUrl: string): Promise<string> {
  if (signedUrlCache.has(gsUrl)) {
    return signedUrlCache.get(gsUrl)!;
  }

  if (!gsUrl.startsWith('gs://')) {
    signedUrlCache.set(gsUrl, gsUrl);
    return gsUrl;
  }

  const functions = getFunctions();
  const getSignedDownloadURL = httpsCallable(functions, 'getSignedDownloadURL');
  const result = await getSignedDownloadURL({ url: gsUrl });
  const presignedUrl = (result.data as { presignedUrl: string }).presignedUrl;

  signedUrlCache.set(gsUrl, presignedUrl);
  return presignedUrl;
}

export function useSignedUrls(
  images: ImageWithUrl[],
  options?: UseSignedUrlsOptions
): Record<string, string> {
  const useThumbnails = options?.useThumbnails ?? false;
  const retryAttempts = useRef<Map<string, number>>(new Map());
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const cacheKey = useMemo(() => {
    if (images.length === 0) return null;
    const ids = images.map((img) => img.id).sort().join(',');
    return `signed-urls:${useThumbnails}:${ids}`;
  }, [images, useThumbnails]);

  const fetcher = useCallback(async (): Promise<Record<string, string>> => {
    const results: Record<string, string> = {};
    await Promise.all(
      imagesRef.current.map(async (img) => {
        if (!img.url) return;
        const gsUrl = useThumbnails ? getThumbnailUrl(img.url) : img.url;
        try {
          results[img.id] = await fetchSignedUrl(gsUrl);
        } catch (err) {
          console.error(`Error fetching signed URL for ${img.id}`, err);
        }
      })
    );
    return results;
  }, [useThumbnails]);

  const { data: urls = {}, mutate } = useSWR(cacheKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 60000,
  });

  const onLoadError = useCallback((imageId: string) => {
    if (!useThumbnails) return;

    const attempts = retryAttempts.current.get(imageId) || 0;
    if (attempts >= 5) {
      console.log(`Max retries reached for thumbnail ${imageId}`);
      return;
    }

    const delay = 3000 * Math.pow(2, attempts); // 3s, 6s, 12s, 24s, 48s
    retryAttempts.current.set(imageId, attempts + 1);

    console.log(`Thumbnail 404 for ${imageId}, retry ${attempts + 1}/5 in ${delay / 1000}s`);

    setTimeout(() => {
      const img = imagesRef.current.find((i) => i.id === imageId);
      if (!img) return;

      // Clear cache for this thumbnail
      const gsUrl = getThumbnailUrl(img.url);
      signedUrlCache.delete(gsUrl);

      // Trigger SWR refetch
      mutate();
    }, delay);
  }, [useThumbnails, mutate]);

  // Attach onLoadError to result for component access
  const result = urls as Record<string, string> & { onLoadError?: (id: string) => void };
  result.onLoadError = onLoadError;

  return result;
}
